import Icon from "./Icon";
import { formatEventDate, formatWindow, shopTitle } from "./nsdFields";
import "./events.css";

/* Accepts a 12-hour slot label ("8:00 AM") or a 24-hour time ("09:00"). */
const toMinutes = (t) => {
  if (!t) return null;
  const s = String(t).trim();
  const h12 = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(s);
  if (h12) {
    const h = (Number(h12[1]) % 12) + (/pm/i.test(h12[3]) ? 12 : 0);
    return h * 60 + Number(h12[2]);
  }
  const h24 = /^(\d{1,2}):(\d{2})$/.exec(s);
  return h24 ? Number(h24[1]) * 60 + Number(h24[2]) : null;
};

/* Floating-time stamp YYYYMMDDTHHMMSS (no TZ — the event is local to the shop). */
const stamp = (eventDate, minutes) => {
  const d = eventDate.replace(/-/g, "");
  const pad = (n) => String(n).padStart(2, "0");
  return `${d}T${pad(Math.floor(minutes / 60))}${pad(minutes % 60)}00`;
};

/**
 * A Classic booking is the guest's own slot; a Custom signup has no slot, so the
 * invite spans the whole event window instead.
 */
const buildCalendarTimes = (data) => {
  const ev = data.event;
  const slot = toMinutes(data.slotTime);
  const startMinutes = slot !== null ? slot : toMinutes(ev.startTime) ?? 9 * 60;
  const endMinutes =
    slot !== null
      ? startMinutes + (data.slotMinutes || 60)
      : toMinutes(ev.endTime) ?? startMinutes + 60;
  return {
    start: stamp(ev.eventDate, startMinutes),
    end: stamp(ev.eventDate, endMinutes),
  };
};

/* Off-site events carry their own location; otherwise it's the shop. */
const calendarLocation = (ev) =>
  ev.location || ev.shop?.address || shopTitle(ev.shop?.name) || "";

const calendarDescription = (data) => {
  const ev = data.event;
  const lines = [];
  if (ev.partner) lines.push(`Hosted with ${ev.partner}`);
  if (data.slotTime) lines.push(`Arrival time: ${data.slotTime}`);
  const shop = shopTitle(ev.shop?.name);
  if (shop) {
    lines.push(
      `Questions? Contact ${shop}${
        ev.shop?.phone ? ` at ${ev.shop.phone}` : ""
      }`
    );
  }
  return lines.join("\n");
};

/* Commas, semicolons and backslashes are value separators in iCalendar text. */
const icsEscape = (s) =>
  String(s ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

const downloadICS = (data) => {
  const { start, end } = buildCalendarTimes(data);
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CBA//NSD Events//EN",
    "BEGIN:VEVENT",
    `UID:${data.event.event_id}-${start}@cbac.com`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${icsEscape(data.event.name)}`,
    `LOCATION:${icsEscape(calendarLocation(data.event))}`,
    `DESCRIPTION:${icsEscape(calendarDescription(data))}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "event.ics";
  a.click();
  URL.revokeObjectURL(url);
};

const googleUrl = (data) => {
  const { start, end } = buildCalendarTimes(data);
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: data.event.name,
    dates: `${start}/${end}`,
    location: calendarLocation(data.event),
    details: calendarDescription(data),
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
};

const Confirmation = ({ data }) => {
  const vehicle = [data.year, data.make, data.model].filter(Boolean).join(" ");
  const vehicleId = data.idType === "vin" ? data.vin : data.plate;

  return (
    <div className="ev-wrap ev-wrap--narrow">
      <div className="ev-confirm-center">
        <div className="ev-confirm-badge">
          <Icon
            name="check"
            size={38}
            color="var(--brand-legacy-green)"
            stroke={2.5}
          />
        </div>
        <div className="ev-eyebrow">
          {data.slotTime ? "You're booked!" : "You're signed up!"}
        </div>
        <h1 className="ev-h1">
          {data.slotTime ? "Your spot is reserved" : "Thanks for signing up"},{" "}
          {data.firstName || "friend"}!
        </h1>
      </div>

      <div className="ev-confirm-card">
        <div className="ev-confirm-card-title">{data.event.name}</div>
        <div className="ev-confirm-line">
          <Icon name="calendar" color="var(--brand-legacy-green)" />{" "}
          {formatEventDate(data.event.eventDate)}
        </div>
        <div className="ev-confirm-line">
          <Icon name="clock" color="var(--brand-legacy-green)" />{" "}
          {data.slotTime
            ? `Arrive at ${data.slotTime}`
            : formatWindow(data.event.startTime, data.event.endTime)}
        </div>
        <div className="ev-confirm-line">
          <Icon name="pin" color="var(--brand-legacy-green)" />{" "}
          {data.event.location || shopTitle(data.event.shop?.name)}
        </div>
        {vehicle && (
          <div className="ev-confirm-line">
            <Icon name="car" color="var(--brand-legacy-green)" /> {vehicle}
            {vehicleId ? ` · ${vehicleId}` : ""}
          </div>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="ev-field-label">Add it to your calendar</div>
        <div className="ev-actions" style={{ marginTop: 10 }}>
          <a
            className="ev-btn ev-btn--secondary"
            href={googleUrl(data)}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="calendar" size={18} /> Google Calendar
          </a>
          <button
            className="ev-btn ev-btn--secondary"
            onClick={() => downloadICS(data)}
          >
            <Icon name="calendar" size={18} /> Apple / Outlook (.ics)
          </button>
        </div>
      </div>

      <p className="ev-confirm-foot">
        A confirmation is on its way to your email. You can cancel anytime using
        the link in that email. Questions? Call us at{" "}
        <strong>{data.event.shop?.phone}</strong>.
      </p>
    </div>
  );
};

export default Confirmation;
